import { ChevronUp, Send, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button, Spinner } from '@respectly/ui';

interface MobileSigningBarProps {
  signedCount: number;
  requiredCount: number;
  canSubmit: boolean;
  isSubmitting: boolean;
  onComplete: () => void;
  onDecline: () => void;
  onOpenDetails: () => void;
}

export function MobileSigningBar({
  signedCount,
  requiredCount,
  canSubmit,
  isSubmitting,
  onComplete,
  onDecline,
  onOpenDetails,
}: MobileSigningBarProps) {
  const progress =
    requiredCount > 0
      ? Math.min(1, signedCount / requiredCount)
      : signedCount > 0
        ? 1
        : 0;
  const progressPct = Math.round(progress * 100);
  const ready = canSubmit;

  return (
    <div className="relative flex-shrink-0 border-t border-gray-200 bg-white/95 shadow-[0_-6px_24px_rgba(15,23,42,0.08)] backdrop-blur-md">
      <button
        type="button"
        onClick={onOpenDetails}
        aria-label="Show signing details"
        className="group flex w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
      >
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
            {ready ? (
              <span className="text-green-600">Ready to sign</span>
            ) : (
              <>
                <span className="text-gray-900">{signedCount}</span>
                <span className="text-gray-400"> / </span>
                <span>{requiredCount} required</span>
              </>
            )}
          </p>
          <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-gray-100">
            <motion.div
              className={`h-full rounded-full ${
                ready ? 'bg-green-500' : 'bg-primary-500'
              }`}
              initial={false}
              animate={{ width: `${progressPct}%` }}
              transition={{ type: 'spring', damping: 28, stiffness: 220 }}
            />
          </div>
        </div>
        <span className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-gray-500 transition-colors group-hover:text-gray-700">
          Details
          <ChevronUp className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5" />
        </span>
      </button>

      <div className="safe-bottom flex items-center gap-2 px-4 pt-1 pb-3">
        <Button
          variant="ghost"
          onClick={onDecline}
          disabled={isSubmitting}
          className="h-11 min-w-[88px] px-3 text-gray-500 hover:bg-red-50 hover:text-red-600"
        >
          <X className="mr-1.5 h-4 w-4" />
          Decline
        </Button>

        <Button
          variant="default"
          onClick={ready ? onComplete : onOpenDetails}
          disabled={isSubmitting}
          className="h-11 flex-1 text-base font-semibold shadow-sm"
        >
          {isSubmitting ? (
            <Spinner size="sm" className="mr-2" />
          ) : (
            <Send className="mr-2 h-4 w-4" />
          )}
          {ready ? 'Complete Signing' : 'Sign'}
        </Button>
      </div>
    </div>
  );
}
